import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@auth/AuthProvider';
import PlaylistsController from '@data/controller/PlaylistsController';
import Playlist from '@data/interfaces/playlist.interface';
import { SongAttributes } from '@data/interfaces/song.interface';

export default function usePlaylist(playlistID?: string, providedChurchId?: string, initialPlaylist?: Playlist | null) {
  const auth = useAuth();
  const churchId = providedChurchId || auth.churchId;

  const [playlist, setPlaylist] = useState<Playlist | null>(initialPlaylist || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);

  useEffect(() => {
    setPlaylist(initialPlaylist || null);
  }, [initialPlaylist]);

  const fetch = useCallback(async () => {
    if (!churchId || !playlistID) return;

    setLoading(true);
    setError(null);
    setHasLoaded(false);

    try {
      const response = await PlaylistsController.fetchPlaylist(churchId, playlistID, auth);
      if (response.error) {
        setError(response.error);
        setPlaylist(null);
      } else if (response.body) {
        setPlaylist(response.body);
      } else {
        setPlaylist(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load playlist');
      setPlaylist(null);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [churchId, playlistID, auth]);

  const addSong = useCallback(async (songAttributes: SongAttributes) => {
    if (!churchId || !playlistID) return {
      success: false,
      title: '',
      message: 'Failed to load playlist for update.'
    }

    const response = await PlaylistsController.addSongToPlaylist(
      churchId,
      playlistID,
      songAttributes,
      auth
    );

    if (!response || response.error || !response.body) {
      return {
        success: false,
        title: '',
        message: response?.error || 'An unknown error occurred while adding the song to the playlist.'
      }
    }

    const updatedPlaylist = response.body.result;
    setPlaylist(updatedPlaylist);

    return {
      success: true,
      title: 'Added to Playlist',
      message: `${songAttributes.title} was added to ${updatedPlaylist.attributes.name}.`
    }
  }, [churchId, playlistID, auth]);

  const removeSong = useCallback(async (songID: string) => {
    if (!churchId || !playlistID) {
      return {
        success: false,
        title: '',
        message: 'Failed to load playlist for update.'
      };
    }

    const response = await PlaylistsController.removeSongFromPlaylist(
      churchId,
      playlistID,
      songID,
      auth
    );

    if (!response || response.error) {
      return {
        success: false,
        title: '',
        message: response?.error || 'An unknown error occurred while removing the song from the playlist.'
      };
    }

    setPlaylist((currentPlaylist) => {
      if (!currentPlaylist) {
        return currentPlaylist;
      }

      return {
        ...currentPlaylist,
        songs: currentPlaylist.songs.filter((song) => song.id !== songID)
      };
    });

    return {
      success: true,
      title: 'Removed from Playlist',
      message: 'The song was removed from the playlist.'
    };
  }, [churchId, playlistID, auth]);

  useEffect(() => {
    if (!churchId || !playlistID) return;

    void fetch();
  }, [churchId, playlistID, fetch]);

  return { playlist, loading, error, hasLoaded, refetch: fetch, addSong, removeSong } as const;
}
